import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { ReservationsService } from '@/api/services/reservations.service';
import {
  DEFAULT_TABLE_SEATS,
  adjustSeats as adjustDraftSeats,
  applyTablesDraft,
  dirtyTableIds,
  pruneTablesDraft,
  tablesBatchEntries,
  withMode,
  withSeats,
  withSeatsForOnline,
} from '@/domain/reservations/reservation-tables-draft';
import type {
  IReservationTableDraftValues,
  TReservationTablesDraftValues,
} from '@/domain/reservations/reservation-tables-draft';
import type { IReservationTableRef } from '@/interfaces/IReservation';
import type {
  IReservationSettingsConfiguration,
  TTableReservationsMode,
} from '@/interfaces/IReservationSettings';
import type { ITable } from '@/interfaces/ITable';
import { useRestaurantStore } from '@/stores/restaurant.store';

export interface IReservationTablesDraftOptions {
  /** Coalesces every edit into one request after this delay; omitted = manual `save()`. */
  autosaveMs?: number;
  onSaved?: (tables: ITable[]) => void;
  onError?: (cause: unknown) => void;
}

export interface IReservationTablesDraft {
  /** The server tables with the overlay applied. */
  tables: ComputedRef<IReservationTableRef[]>;
  /** Overlay of the user's edits only; starts as {}. */
  values: Readonly<Ref<TReservationTablesDraftValues>>;
  dirtyIds: ComputedRef<number[]>;
  dirty: ComputedRef<boolean>;
  saving: Ref<boolean>;
  failed: Ref<boolean>;
  setSeats(ids: readonly number[], capacity: number): void;
  adjustSeats(ids: readonly number[], delta: number): void;
  setMode(
    ids: readonly number[],
    mode: TTableReservationsMode,
    defaultSeats?: number
  ): void;
  applyToAll(capacity: number): void;
  /** Replaces the overlay wholesale; no-op when the current overlay already has any key. */
  seed(next: TReservationTablesDraftValues): void;
  /** Replaces the overlay wholesale (used by «Deshacer» of «Poner en todas»). */
  restore(values: TReservationTablesDraftValues): void;
  discard(): void;
  /**
   * One PUT with the dirty tables; false on failure or when the scope changed
   * meanwhile. While a save is in flight it returns that same promise.
   */
  save(): Promise<boolean>;
  /** Cancels the autosave timer, saves what is dirty and resolves when idle. */
  flush(): Promise<boolean>;
}

function toTableRef(row: ITable): IReservationTableRef {
  return {
    id: row.id,
    name: row.name,
    capacity: row.capacity,
    sectorId: row.sectorId,
    enabled: row.enabled,
    reservationsMode: row.reservationsMode ?? 'online',
  };
}

function toDraftValues(row: IReservationTableRef): IReservationTableDraftValues {
  return { capacity: row.capacity, reservationsMode: row.reservationsMode };
}

/**
 * The tables draft behind the setup guide (manual save) and the Ajustes
 * tables card (autosave). The overlay only holds touched tables, so a change
 * made elsewhere to an untouched table is never overwritten nor marked dirty.
 */
export function useReservationTablesDraft(
  configuration: () => IReservationSettingsConfiguration | null,
  options: IReservationTablesDraftOptions = {}
): IReservationTablesDraft {
  const store = useRestaurantStore();
  const values = shallowRef<TReservationTablesDraftValues>({});
  /** Rows the last successful PUT returned: the draft is clean against them until the reload lands. */
  const lastSaved = shallowRef<ReadonlyMap<number, IReservationTableRef>>(
    new Map()
  );
  const saving = ref(false);
  const failed = ref(false);
  let alive = true;
  let generation = 0;
  /** Counts overlay mutations, so flush() can tell "still dirty" from "edited again". */
  let edits = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inflight: Promise<boolean> | null = null;
  let pendingAgain = false;

  const serverTables = () => configuration()?.tables ?? [];
  /** Server rows, with the rows the last save returned in their place. */
  function baselineTables(): IReservationTableRef[] {
    const saved = lastSaved.value;
    return serverTables().map((table) => saved.get(table.id) ?? table);
  }
  const tables = computed(() =>
    applyTablesDraft(serverTables(), values.value)
  );
  const dirtyIds = computed(() =>
    dirtyTableIds(baselineTables(), values.value)
  );
  const dirty = computed(() => dirtyIds.value.length > 0);
  const scopeKey = () => {
    const config = configuration();
    return config
      ? `${config.settings.restaurantId}:${config.settings.branchId}`
      : '';
  };

  function clearTimer() {
    if (timer === undefined) return;
    clearTimeout(timer);
    timer = undefined;
  }
  function storeScope(restaurantId: number, branchId: number): boolean {
    return (
      store.restaurant?.id === restaurantId &&
      store.selectedBranch?.id === branchId
    );
  }
  function current(
    requestGeneration: number,
    restaurantId: number,
    branchId: number
  ): boolean {
    return (
      alive &&
      requestGeneration === generation &&
      storeScope(restaurantId, branchId)
    );
  }
  /** The POS table list shows the saved seats and mode at once. */
  function patchStore(rows: ITable[], restaurantId: number, branchId: number) {
    if (!store.tables) return;
    const byId = new Map(rows.map((row) => [row.id, row]));
    store.setTables(
      store.tables.map((table: ITable) => {
        const row = byId.get(table.id);
        if (
          !row ||
          table.restaurantId !== restaurantId ||
          table.branchId !== branchId
        )
          return table;
        return {
          ...table,
          capacity: row.capacity,
          reservationsMode: row.reservationsMode ?? table.reservationsMode,
        };
      })
    );
  }
  /**
   * What the server stored becomes the overlay value of every table not
   * edited again during the flight, so the draft is exactly as dirty as the
   * server says (a mode-only write on a table that gained seats elsewhere
   * would otherwise never settle) and the tiles show the stored values.
   */
  function adoptSaved(
    sent: TReservationTablesDraftValues,
    rows: readonly IReservationTableRef[]
  ) {
    const next: Record<number, IReservationTableDraftValues> = {
      ...values.value,
    };
    for (const row of rows) {
      const entry = next[row.id];
      if (entry !== undefined && entry === sent[row.id])
        next[row.id] = toDraftValues(row);
    }
    values.value = next;
  }

  async function send(): Promise<boolean> {
    const config = configuration();
    if (!alive || !config) return false;
    const { restaurantId, branchId } = config.settings;
    const requestGeneration = generation;
    const sent = values.value;
    let rows: ITable[] | null = null;
    saving.value = true;
    try {
      const entries = tablesBatchEntries(
        baselineTables(),
        sent,
        dirtyIds.value
      );
      if (entries.length)
        rows = await ReservationsService.saveTables({
          restaurantId,
          branchId,
          entries,
        });
    } catch (cause) {
      if (!current(requestGeneration, restaurantId, branchId)) return false;
      failed.value = true;
      options.onError?.(cause);
      return false;
    } finally {
      if (current(requestGeneration, restaurantId, branchId))
        saving.value = false;
    }
    if (rows === null) return true;
    // The shared store belongs to the branch, not to this view: reconcile it
    // even after an unmount, as long as the branch is still the same.
    if (storeScope(restaurantId, branchId))
      patchStore(rows, restaurantId, branchId);
    if (!current(requestGeneration, restaurantId, branchId)) return false;
    const saved = rows.map(toTableRef);
    const merged = new Map(lastSaved.value);
    for (const row of saved) merged.set(row.id, row);
    lastSaved.value = merged;
    adoptSaved(sent, saved);
    failed.value = false;
    options.onSaved?.(rows);
    return true;
  }

  function save(): Promise<boolean> {
    if (inflight) return inflight;
    if (!dirty.value) return Promise.resolve(true);
    const request: Promise<boolean> = send().then((result) => {
      // A request superseded by a scope reset must not touch the live one.
      if (inflight !== request) return result;
      inflight = null;
      // Edits made while this request was in flight go out right away.
      if (pendingAgain) {
        pendingAgain = false;
        if (result) void save();
      }
      return result;
    });
    inflight = request;
    return request;
  }

  function scheduleSave() {
    if (options.autosaveMs === undefined) return;
    if (inflight) {
      pendingAgain = true;
      return;
    }
    clearTimer();
    timer = setTimeout(() => {
      timer = undefined;
      void save();
    }, options.autosaveMs);
  }

  async function flush(): Promise<boolean> {
    const requestGeneration = generation;
    for (;;) {
      clearTimer();
      if (generation !== requestGeneration) return false;
      if (inflight) {
        if (!(await inflight)) return false;
        continue;
      }
      if (!dirty.value) return true;
      const editsBefore = edits;
      if (!(await save())) return false;
      // A save that succeeded but left the same draft dirty cannot settle by
      // repeating itself: stop instead of looping.
      if (dirty.value && edits === editsBefore) return false;
    }
  }

  function reset() {
    clearTimer();
    generation++;
    pendingAgain = false;
    inflight = null;
    values.value = {};
    lastSaved.value = new Map();
    saving.value = false;
    failed.value = false;
  }

  function replace(next: TReservationTablesDraftValues) {
    values.value = next;
    edits++;
    scheduleSave();
  }
  function setSeats(ids: readonly number[], capacity: number) {
    replace(withSeats(serverTables(), values.value, ids, capacity));
  }
  function adjustSeats(ids: readonly number[], delta: number) {
    replace(adjustDraftSeats(serverTables(), values.value, ids, delta));
  }
  function setMode(
    ids: readonly number[],
    mode: TTableReservationsMode,
    defaultSeats = DEFAULT_TABLE_SEATS
  ) {
    replace(withMode(serverTables(), values.value, ids, mode, defaultSeats));
  }
  function applyToAll(capacity: number) {
    replace(withSeatsForOnline(serverTables(), values.value, capacity));
  }
  function seed(next: TReservationTablesDraftValues) {
    if (Object.keys(values.value).length > 0) return;
    replace(next);
  }
  function restore(next: TReservationTablesDraftValues) {
    replace(next);
  }
  function discard() {
    clearTimer();
    pendingAgain = false;
    values.value = {};
    edits++;
  }

  watch(scopeKey, reset);
  // Committed entries (equal to the last save or to the reloaded row) leave
  // the overlay, so our own save lands without a call from the component; a
  // remote change to an untouched table flows through untouched.
  watch(
    () => configuration()?.tables,
    (list) => {
      if (!list) return;
      const saved = lastSaved.value;
      const committed = list.map((table) => saved.get(table.id) ?? table);
      values.value = pruneTablesDraft(
        list,
        pruneTablesDraft(committed, values.value)
      );
      lastSaved.value = new Map();
    }
  );
  onScopeDispose(() => {
    alive = false;
    clearTimer();
  });

  return {
    tables,
    values,
    dirtyIds,
    dirty,
    saving,
    failed,
    setSeats,
    adjustSeats,
    setMode,
    applyToAll,
    seed,
    restore,
    discard,
    save,
    flush,
  };
}
